/**
 * DOM Utilities
 *
 * Pure utility functions for DOM manipulation in the verification UI.
 * These functions provide type-safe wrappers around common DOM operations.
 */

/**
 * Clears all options from a select element and optionally adds a placeholder.
 *
 * @param select - The select element to clear
 * @param placeholder - Optional placeholder text for the first option
 */
export function clearSelectOptions(select: HTMLSelectElement, placeholder?: string): void {
  select.innerHTML = '';
  if (placeholder) {
    const option = document.createElement('option');
    option.value = '';
    option.textContent = placeholder;
    option.disabled = true;
    option.selected = true;
    select.appendChild(option);
  }
}

/**
 * Option configuration for populating select elements.
 */
export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * Populates a select element with options.
 *
 * @param select - The select element to populate
 * @param options - Array of options to add
 * @param placeholder - Optional placeholder text for the first option
 */
export function populateSelect(
  select: HTMLSelectElement,
  options: SelectOption[],
  placeholder?: string
): void {
  clearSelectOptions(select, placeholder);

  for (const opt of options) {
    const option = document.createElement('option');
    option.value = opt.value;
    option.textContent = opt.label;
    if (opt.disabled) {
      option.disabled = true;
    }
    select.appendChild(option);
  }
}

/**
 * Shows an element by removing the 'hidden' class.
 *
 * @param elementOrId - Element or element ID to show
 */
export function showElement(elementOrId: string | HTMLElement): void {
  const element =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;
  element?.classList.remove('hidden');
}

/**
 * Hides an element by adding the 'hidden' class.
 *
 * @param elementOrId - Element or element ID to hide
 */
export function hideElement(elementOrId: string | HTMLElement): void {
  const element =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;
  element?.classList.add('hidden');
}

/**
 * Toggles the visibility of an element.
 *
 * @param elementOrId - Element or element ID to toggle
 * @param show - If provided, forces show (true) or hide (false)
 */
export function toggleElement(elementOrId: string | HTMLElement, show?: boolean): void {
  const element =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;

  if (!element) return;

  if (show === undefined) {
    element.classList.toggle('hidden');
  } else if (show) {
    element.classList.remove('hidden');
  } else {
    element.classList.add('hidden');
  }
}

/**
 * Enables a button element.
 *
 * @param buttonOrId - Button element or element ID
 */
export function enableButton(buttonOrId: string | HTMLButtonElement): void {
  const button =
    typeof buttonOrId === 'string'
      ? (document.getElementById(buttonOrId) as HTMLButtonElement | null)
      : buttonOrId;
  if (button) {
    button.disabled = false;
  }
}

/**
 * Disables a button element.
 *
 * @param buttonOrId - Button element or element ID
 */
export function disableButton(buttonOrId: string | HTMLButtonElement): void {
  const button =
    typeof buttonOrId === 'string'
      ? (document.getElementById(buttonOrId) as HTMLButtonElement | null)
      : buttonOrId;
  if (button) {
    button.disabled = true;
  }
}

/**
 * Sets the text content of an element.
 *
 * @param elementOrId - Element or element ID
 * @param text - Text content to set
 */
export function setText(elementOrId: string | HTMLElement, text: string): void {
  const element =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;
  if (element) {
    element.textContent = text;
  }
}

/**
 * Adds one or more CSS classes to an element.
 *
 * @param elementOrId - Element or element ID
 * @param classes - Class name(s) to add
 */
export function addClass(elementOrId: string | HTMLElement, ...classes: string[]): void {
  const element =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;
  element?.classList.add(...classes);
}

/**
 * Removes one or more CSS classes from an element.
 *
 * @param elementOrId - Element or element ID
 * @param classes - Class name(s) to remove
 */
export function removeClass(elementOrId: string | HTMLElement, ...classes: string[]): void {
  const element =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;
  element?.classList.remove(...classes);
}

/**
 * Checks if an element has a specific CSS class.
 *
 * @param elementOrId - Element or element ID
 * @param className - Class name to check
 * @returns true if the element has the class
 */
export function hasClass(elementOrId: string | HTMLElement, className: string): boolean {
  const element =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;
  return element?.classList.contains(className) ?? false;
}

/**
 * Sets an attribute on an element.
 *
 * @param elementOrId - Element or element ID
 * @param name - Attribute name
 * @param value - Attribute value
 */
export function setAttribute(elementOrId: string | HTMLElement, name: string, value: string): void {
  const element =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;
  element?.setAttribute(name, value);
}

/**
 * Removes an attribute from an element.
 *
 * @param elementOrId - Element or element ID
 * @param name - Attribute name to remove
 */
export function removeAttribute(elementOrId: string | HTMLElement, name: string): void {
  const element =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;
  element?.removeAttribute(name);
}

/**
 * Gets an element by ID with type casting.
 * Returns null if not found.
 *
 * @param id - Element ID
 * @returns The element or null
 */
export function getElement<T extends HTMLElement = HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

/**
 * Gets an element by ID, throwing if not found.
 * Use this when the element must exist.
 *
 * @param id - Element ID
 * @returns The element
 * @throws Error if element not found
 */
export function getRequiredElement<T extends HTMLElement = HTMLElement>(id: string): T {
  const element = document.getElementById(id) as T | null;
  if (!element) {
    throw new Error(`Required element not found: #${id}`);
  }
  return element;
}

/**
 * Executes a callback when the DOM is ready.
 *
 * @param callback - Function to execute when DOM is ready
 */
export function domReady(callback: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback);
  } else {
    callback();
  }
}

/**
 * Creates elements from an HTML string safely.
 *
 * SECURITY NOTE: This function is for TRUSTED HTML only (internal SDK use).
 * Do NOT use with user-provided content. For user content, create elements
 * programmatically using document.createElement().
 *
 * @param html - Trusted HTML string
 * @returns DocumentFragment containing the parsed elements
 */
export function createFragment(html: string): DocumentFragment {
  const template = document.createElement('template');
  template.innerHTML = html.trim();
  return template.content;
}

/**
 * Scrolls an element into view smoothly.
 *
 * @param elementOrId - Element or element ID
 * @param options - ScrollIntoView options
 */
export function scrollIntoView(
  elementOrId: string | HTMLElement,
  options: ScrollIntoViewOptions = { behavior: 'smooth', block: 'center' }
): void {
  const element =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;
  element?.scrollIntoView(options);
}
